import { useState, useEffect, useCallback, useRef } from 'react';
import * as JsSIP from 'jssip';

export interface WebPhoneCredentials {
  username: string; // custid_extension
  password: string;
  extension: string;
  displayName: string;
  sbcServers: Array<{
    host: string;
    wsUri: string;
    priority: number;
  }>;
}

export interface CallSession {
  session: any;
  direction: 'incoming' | 'outgoing';
  remoteNumber: string;
  startTime: Date;
  duration: number;
  status: 'ringing' | 'connected' | 'ended';
}

export interface RTCStats {
  bytesReceived: number;
  bytesSent: number;
  receiveBitrate: number;
  sendBitrate: number;
  packetsReceived: number;
  packetsSent: number;
  packetsLost: number;
  packetsLostPercentage: string;
  jitter: string;
  roundTripTime: string;
  codec: string;
}

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'registered' | 'error';
export type CallStatus = 'idle' | 'calling' | 'ringing' | 'in-call';

export function useWebPhone() {
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
  const [callStatus, setCallStatus] = useState<CallStatus>('idle');
  const [currentCall, setCurrentCall] = useState<CallSession | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isOnHold, setIsOnHold] = useState(false);
  const [dialedNumber, setDialedNumber] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [audioDevices, setAudioDevices] = useState<{ inputDevices: MediaDeviceInfo[], outputDevices: MediaDeviceInfo[] }>({ inputDevices: [], outputDevices: [] });
  const [selectedSpeaker, setSelectedSpeaker] = useState<string>('default');
  const [selectedMicrophone, setSelectedMicrophone] = useState<string>('default');
  const [volume, setVolume] = useState<number>(70);
  const [rtcStats, setRtcStats] = useState<RTCStats | null>(null);
  const [eventLogs, setEventLogs] = useState<Array<{ timestamp: string; message: string; type: 'info' | 'success' | 'error' | 'warning' }>>([]);
  const [debugMode, setDebugMode] = useState<boolean>(() => localStorage.getItem('webphone_debug_mode') === 'true');

  const userAgentRef = useRef<any>(null);
  const currentSessionRef = useRef<any>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const sbcHostRef = useRef<string>('sbc15.iatelecom.ca'); // Store current SBC host

  // Enumerate audio devices
  const enumerateDevices = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();

      // Filter and clean up device lists
      const filterDevices = (deviceList: MediaDeviceInfo[]) => {
        // Remove devices without labels (usually means permission not granted)
        const withLabels = deviceList.filter(device => device.label && device.label.trim() !== '');

        // Remove duplicate labels
        const uniqueDevices = withLabels.filter((device, index, self) =>
          index === self.findIndex(d => d.label === device.label)
        );

        return uniqueDevices;
      };

      const inputDevices = filterDevices(devices.filter(device => device.kind === 'audioinput'));
      const outputDevices = filterDevices(devices.filter(device => device.kind === 'audiooutput'));

      setAudioDevices({ inputDevices, outputDevices });
      console.log('[WebPhone] Audio devices:', { inputDevices, outputDevices });
    } catch (error) {
      console.error('[WebPhone] Failed to enumerate devices:', error);
    }
  }, []);

  // Initialize audio element
  useEffect(() => {
    const audio = new Audio();
    audio.id = 'webphone-remote-audio'; // ← FIX: Add ID so confirmed handler can find it
    audio.autoplay = true;
    audio.volume = 0.7; // Default volume 70%
    audio.muted = false; // Make sure not muted
    audio.style.display = 'none'; // Hide audio element
    document.body.appendChild(audio); // ← CRITICAL: Must append to DOM!
    remoteAudioRef.current = audio;

    console.log('[WebPhone] Audio element initialized and appended to DOM:', {
      autoplay: audio.autoplay,
      volume: audio.volume,
      muted: audio.muted,
      inDOM: document.body.contains(audio)
    });

    // Enumerate devices on mount
    enumerateDevices();

    // Listen for device changes
    navigator.mediaDevices.addEventListener('devicechange', enumerateDevices);

    return () => {
      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = null;
        // Remove from DOM on cleanup
        if (document.body.contains(remoteAudioRef.current)) {
          document.body.removeChild(remoteAudioRef.current);
        }
      }
      navigator.mediaDevices.removeEventListener('devicechange', enumerateDevices);
    };
  }, [enumerateDevices]);

  // Update volume when it changes
  useEffect(() => {
    if (remoteAudioRef.current) {
      remoteAudioRef.current.volume = volume / 100;
      console.log('[WebPhone] Volume changed to:', volume);
    }
  }, [volume]);

  // Update speaker when selection changes
  useEffect(() => {
    if (remoteAudioRef.current && selectedSpeaker && typeof (remoteAudioRef.current as any).setSinkId === 'function') {
      (remoteAudioRef.current as any).setSinkId(selectedSpeaker)
        .then(() => {
          console.log('[WebPhone] Speaker changed to:', selectedSpeaker);
        })
        .catch((error: any) => {
          console.error('[WebPhone] Failed to change speaker:', error);
        });
    }
  }, [selectedSpeaker]);

  // Setup session event handlers
  const setupSessionHandlers = useCallback((session: any, direction: 'incoming' | 'outgoing', remoteNumber: string) => {
    const callSession: CallSession = {
      session,
      direction,
      remoteNumber,
      startTime: new Date(),
      duration: 0,
      status: 'ringing',
    };

    setCurrentCall(callSession);
    currentSessionRef.current = session;

    session.on('progress', () => {
      console.log('[WebPhone] Call in progress');
      setCallStatus('calling');
      setCurrentCall(prev => prev ? { ...prev, status: 'ringing' } : null);
    });

    session.on('accepted', () => {
      console.log('[WebPhone] Call accepted');
      setCallStatus('in-call');
      setCurrentCall(prev => prev ? { ...prev, status: 'connected', startTime: new Date() } : null);
    });

    // Note: confirmed handler is in eventHandlers passed to call()

    session.on('ended', () => {
      console.log('[WebPhone] Call ended');
      setCallStatus('idle');
      setCurrentCall(null);
      currentSessionRef.current = null;
      setIsMuted(false);
      setIsOnHold(false);
      setDialedNumber('');
    });

    session.on('failed', (e: any) => {
      console.error('[WebPhone] Call failed:', e);
      console.error('[WebPhone] Failed event details:', {
        cause: e.cause,
        originator: e.originator,
        status_code: e.message?.status_code,
        reason_phrase: e.message?.reason_phrase,
        headers: e.message?.headers
      });
      setCallStatus('idle');
      setCurrentCall(null);
      currentSessionRef.current = null;
      setErrorMessage(`Call failed: ${e.cause || 'Unknown error'}`);
      setTimeout(() => setErrorMessage(null), 5000);
    });

    // Note: peerconnection handler is in eventHandlers passed to call()

    // Handle hold/unhold events
    session.on('hold', (e: any) => {
      console.log('[WebPhone] Call put on hold', e);
      setIsOnHold(true);
    });

    session.on('unhold', (e: any) => {
      console.log('[WebPhone] Call resumed from hold', e);
      setIsOnHold(false);
    });
  }, [selectedSpeaker]);

  // Connect to WebRTC
  const connect = useCallback(async (credentials: WebPhoneCredentials) => {
    try {
      setConnectionStatus('connecting');
      setErrorMessage(null);

      // Request microphone permission early to get device labels
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach(track => track.stop());
        // Enumerate devices again after permission granted
        await enumerateDevices();
      } catch (error) {
        console.warn('[WebPhone] Microphone permission not granted yet:', error);
      }

      // Try primary SBC first
      const primarySBC = credentials.sbcServers.find(s => s.priority === 1) || credentials.sbcServers[0];

      // Store SBC host for use in makeCall
      sbcHostRef.current = primarySBC.host;

      const socket = new JsSIP.WebSocketInterface(primarySBC.wsUri);

      const configuration = {
        sockets: [socket],
        uri: `sip:${credentials.username}@${primarySBC.host}`,
        authorization_user: credentials.username,
        password: credentials.password,
        display_name: credentials.displayName,
        register: true,
        session_timers: false,
        rtcpMuxPolicy: 'require',
        // Enable automatic handling of 407 Proxy Authentication
        realm: primarySBC.host,
        ha1: null, // Let JsSIP compute auth from username/password
        pcConfig: {
          iceServers: [],
          iceTransportPolicy: 'all',
          bundlePolicy: 'max-bundle',
          rtcpMuxPolicy: 'require',
        },
      };

      console.log('[WebPhone] JsSIP Configuration:', {
        uri: configuration.uri,
        authorization_user: configuration.authorization_user,
        realm: configuration.realm,
        display_name: configuration.display_name
      });

      const userAgent = new JsSIP.UA(configuration);
      userAgentRef.current = userAgent;

      // Event handlers
      userAgent.on('connected', () => {
        console.log('[WebPhone] WebSocket connected');
        setConnectionStatus('connected');
      });

      userAgent.on('disconnected', () => {
        console.log('[WebPhone] WebSocket disconnected');
        setConnectionStatus('disconnected');
      });

      userAgent.on('registered', () => {
        console.log('[WebPhone] SIP registered');
        setConnectionStatus('registered');
      });

      userAgent.on('registrationFailed', (e: any) => {
        console.error('[WebPhone] Registration failed:', e);
        console.error('[WebPhone] Registration failure details:', {
          cause: e.cause,
          response: e.response,
          status_code: e.response?.status_code,
          reason_phrase: e.response?.reason_phrase,
        });

        // If it's a connection error (not auth), retry once after a delay
        if (e.cause === 'Connection Error' || e.cause === 'Request Timeout' || !e.response) {
          console.log('[WebPhone] Connection error detected, will retry in 2 seconds...');
          setTimeout(() => {
            console.log('[WebPhone] Retrying registration...');
            if (userAgentRef.current) {
              userAgentRef.current.start();
            }
          }, 2000);
        } else {
          setConnectionStatus('error');
          const errorMsg = e.response?.reason_phrase || e.cause || 'Unknown error';
          setErrorMessage(`Registration failed: ${errorMsg}`);
        }
      });

      userAgent.on('newRTCSession', (data: any) => {
        const session = data.session;

        if (session.direction === 'incoming') {
          // Incoming call
          console.log('[WebPhone] Incoming call from:', session.remote_identity.uri.user);
          const remoteNumber = session.remote_identity.uri.user;

          setCallStatus('ringing');
          setupSessionHandlers(session, 'incoming', remoteNumber);

          // Auto-answer could be added here or show notification
          // For now, we'll need to call answerCall() manually
        }
      });

      // Wait for WebSocket to be fully connected before starting
      userAgent.on('connected', () => {
        console.log('[WebPhone] WebSocket connected, ready for registration');
      });

      userAgent.start();
    } catch (error) {
      console.error('[WebPhone] Connect error:', error);
      setConnectionStatus('error');
      setErrorMessage(`Connection error: ${error}`);
    }
  }, [setupSessionHandlers, enumerateDevices]);

  // Disconnect
  const disconnect = useCallback(() => {
    if (currentSessionRef.current) {
      currentSessionRef.current.terminate();
    }
    if (userAgentRef.current) {
      userAgentRef.current.stop();
    }
    setConnectionStatus('disconnected');
    setCallStatus('idle');
    setCurrentCall(null);
    setDialedNumber('');
  }, []);

  // Make a call
  const makeCall = useCallback(async (number: string) => {
    if (!userAgentRef.current || connectionStatus !== 'registered') {
      setErrorMessage('Not connected. Please wait for registration.');
      return;
    }

    if (!number || number.trim() === '') {
      setErrorMessage('Please enter a number');
      return;
    }

    try {
      // Request microphone permission first
      console.log('[WebPhone] Requesting microphone permission...');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      console.log('[WebPhone] Microphone permission granted');

      // Stop the test stream - JsSIP will create its own
      stream.getTracks().forEach(track => track.stop());

      const eventHandlers = {
        progress: () => console.log('[WebPhone] Call is in progress'),
        failed: (e: any) => console.log('[WebPhone] Call failed:', e),
        ended: (e: any) => console.log('[WebPhone] Call ended:', e),
        accepted: () => console.log('[WebPhone] Call accepted'),
        peerconnection: (e: any) => {
          const peerconnection = e.peerconnection;
          console.log('[WebPhone] ========== PEERCONNECTION EVENT ==========');

          peerconnection.ontrack = async (event: RTCTrackEvent) => {
            console.log('[WebPhone] Remote track received:', {
              kind: event.track.kind,
              enabled: event.track.enabled,
              muted: event.track.muted,
              readyState: event.track.readyState,
              streams: event.streams.length
            });

            if (remoteAudioRef.current && event.streams[0]) {
              const stream = event.streams[0];
              remoteAudioRef.current.srcObject = stream;

              console.log('[WebPhone] Audio stream attached:', {
                streamActive: stream.active,
                trackCount: stream.getTracks().length,
                tracks: stream.getTracks().map(t => ({
                  kind: t.kind,
                  enabled: t.enabled,
                  muted: t.muted,
                  readyState: t.readyState
                })),
                audioElement: {
                  volume: remoteAudioRef.current.volume,
                  muted: remoteAudioRef.current.muted,
                  paused: remoteAudioRef.current.paused,
                  readyState: remoteAudioRef.current.readyState,
                  networkState: remoteAudioRef.current.networkState
                }
              });
            }
          };
        },
        confirmed: () => {
          console.log('[WebPhone] ========== CONFIRMED EVENT FIRED ==========');
          console.log('[WebPhone] Call confirmed - media should be flowing');
        }
      };

      const options = {
        eventHandlers,
        mediaConstraints: { audio: true, video: false },
      };

      // Use the stored SBC host from connection
      const sipUri = `sip:${number}@${sbcHostRef.current}`;
      console.log('[WebPhone] Making call to:', sipUri);

      const session = userAgentRef.current.call(sipUri, options);
      setupSessionHandlers(session, 'outgoing', number);
    } catch (error: any) {
      console.error('[WebPhone] Media error:', error);
      setErrorMessage(`Microphone access denied: ${error.message}`);
      setTimeout(() => setErrorMessage(null), 5000);
    }
  }, [connectionStatus, setupSessionHandlers]);

  // Answer incoming call
  const answerCall = useCallback(() => {
    if (currentSessionRef.current && currentCall?.direction === 'incoming') {
      currentSessionRef.current.answer({
        mediaConstraints: { audio: true, video: false },
      });
    }
  }, [currentCall]);

  // Hang up
  const hangUp = useCallback(() => {
    if (currentSessionRef.current) {
      currentSessionRef.current.terminate();
    }
  }, []);

  // Mute/unmute
  const toggleMute = useCallback(() => {
    if (currentSessionRef.current && currentSessionRef.current.isEstablished()) {
      if (isMuted) {
        currentSessionRef.current.unmute();
      } else {
        currentSessionRef.current.mute();
      }
      setIsMuted(!isMuted);
    }
  }, [isMuted]);

  // Hold/unhold
  const toggleHold = useCallback(() => {
    if (currentSessionRef.current && currentSessionRef.current.isEstablished()) {
      try {
        if (isOnHold) {
          console.log('[WebPhone] Unholding call...');
          currentSessionRef.current.unhold();
        } else {
          console.log('[WebPhone] Holding call...');
          currentSessionRef.current.hold();
        }
        // State will be updated by 'hold' and 'unhold' event handlers
      } catch (error) {
        console.error('[WebPhone] Hold/unhold failed:', error);
        setErrorMessage(`Hold operation failed: ${error}`);
        setTimeout(() => setErrorMessage(null), 3000);
      }
    }
  }, [isOnHold]);

  // Send DTMF
  const sendDTMF = useCallback((digit: string) => {
    if (currentSessionRef.current && currentSessionRef.current.isEstablished()) {
      currentSessionRef.current.sendDTMF(digit);
    }
    // Also update dialed number during call
    if (callStatus === 'in-call') {
      setDialedNumber(prev => prev + digit);
    }
  }, [callStatus]);

  // Add digit to dialed number
  const addDigit = useCallback((digit: string) => {
    setDialedNumber(prev => prev + digit);
    // If in call, also send as DTMF
    if (callStatus === 'in-call') {
      sendDTMF(digit);
    }
  }, [callStatus, sendDTMF]);

  // Clear dialed number
  const clearNumber = useCallback(() => {
    setDialedNumber('');
  }, []);

  // Change speaker device
  const changeSpeaker = useCallback((deviceId: string) => {
    setSelectedSpeaker(deviceId);
  }, []);

  // Change microphone device
  const changeMicrophone = useCallback((deviceId: string) => {
    setSelectedMicrophone(deviceId);
    // Note: Microphone change will take effect on next call
    // For active calls, we would need to renegotiate the stream
  }, []);

  // Test speaker by playing a tone
  const testSpeaker = useCallback(async () => {
    try {
      // Create an audio context and generate a test tone
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      // Set up a 440Hz tone (A4 note) for 0.5 seconds
      oscillator.frequency.value = 440;
      oscillator.type = 'sine';
      gainNode.gain.value = 0.3;

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);

      console.log('[WebPhone] Test tone played');

      // Clean up
      setTimeout(() => {
        audioContext.close();
      }, 600);
    } catch (error) {
      console.error('[WebPhone] Failed to play test tone:', error);
    }
  }, []);

  // Update call duration
  useEffect(() => {
    if (currentCall && currentCall.status === 'connected') {
      const interval = setInterval(() => {
        setCurrentCall(prev => {
          if (!prev || prev.status !== 'connected') return prev;
          const duration = Math.floor((new Date().getTime() - prev.startTime.getTime()) / 1000);
          return { ...prev, duration };
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [currentCall?.status]);

  const changeVolume = useCallback((newVolume: number) => {
    setVolume(newVolume);
    if (remoteAudioRef.current) {
      remoteAudioRef.current.volume = newVolume / 100;
    }
  }, []);

  const toggleDebugMode = useCallback(() => {
    setDebugMode(prev => {
      const newValue = !prev;
      localStorage.setItem('webphone_debug_mode', String(newValue));
      return newValue;
    });
  }, []);

  return {
    connectionStatus,
    callStatus,
    currentCall,
    isMuted,
    isOnHold,
    dialedNumber,
    errorMessage,
    audioDevices,
    selectedSpeaker,
    selectedMicrophone,
    volume,
    rtcStats,
    eventLogs,
    debugMode,
    connect,
    disconnect,
    makeCall,
    answerCall,
    hangUp,
    toggleMute,
    toggleHold,
    sendDTMF,
    addDigit,
    clearNumber,
    changeSpeaker,
    changeMicrophone,
    changeVolume,
    enumerateDevices,
    testSpeaker,
    toggleDebugMode,
  };
}
